/**
 * Roly obchodník / rozpočtár (KOLO 231) — čo smie prihlásený upravovať.
 *
 * Práva počíta **server** (`services/opravnenia`) a posiela ich v
 * `GET /api/auth/me`; frontend pravidlá neopakuje, len podľa nich skryje
 * alebo zamkne ovládanie. Server zmenu bez práva odmietne tak či tak (403).
 *
 * | | Výpočty | Výroba | Tovar | Podmienky | Nastavenia výroby | Cenník výroby | Cenník tovaru |
 * |---|---|---|---|---|---|---|---|
 * | Rozpočtár | všetko (bez podmienok) | všetko | číta | číta | áno | áno | číta |
 * | Obchodník | len ceny | len ceny | všetko | áno | číta | číta | áno |
 * | Konštruktér (KOLO 239) | číta | všetko | číta | číta | áno | číta | číta |
 * | Admin, bez prihlásenia | všetko | všetko | všetko | áno | áno | áno | áno |
 */
import type { components } from "@/api/types.gen";

export type Prava = components["schemas"]["PravaInfo"];

/** Bez prihlásenia, admin a kým `/me` nedobehne — rozhoduje server. */
export const PLNE_PRAVA: Prava = {
  nabytok: "vsetko",
  vyroba: "vsetko",
  tovar: "vsetko",
  podmienky: true,
  cennik_vyroby: true,
  cennik_tovaru: true,
  nastavenia_vyroby: true,
  rola: null,
};

export function pravaZMe(me: { prava?: Prava | null } | null | undefined): Prava {
  return me?.prava ?? PLNE_PRAVA;
}

/**
 * Ako sa dá pracovať s variantou — `plny`, `len_ceny` (obchodník
 * v nábytku: zľavy, ručná cena dielca, zisk, koeficient) alebo `citanie`.
 */
export type RezimVarianty = "plny" | "len_ceny" | "citanie";

/**
 * Rozsah práv vo variante — tovar, výroba (výrobná varianta nábytku)
 * alebo výpočet nábytku; výrobu od výpočtov oddelilo KOLO 239.
 */
export function rezimVarianty(
  prava: Prava,
  jeTovar: boolean,
  jeVyrobna = false,
): RezimVarianty {
  const r = jeTovar ? prava.tovar : jeVyrobna ? prava.vyroba : prava.nabytok;
  return r === "vsetko" ? "plny" : r === "ceny" ? "len_ceny" : "citanie";
}

/** Vety ako na serveri — do tooltipov zamknutého ovládania. */
export const VETA = {
  nabytok:
    "Nábytok upravuje rozpočtár — ty v ňom meníš len cenu (zľavu, ručnú cenu dielca, zisk, koeficient).",
  vypocet: "Výpočet nábytku upravuje rozpočtár — ty ho vidíš len na čítanie, aj s cenami.",
  tovar: "Tovar upravuje obchodník — ty ho vidíš len na čítanie.",
  podmienky: "Koeficient a zľavu ponuky nastavuje obchodník — ty ich vidíš len na čítanie.",
  vyroba:
    "Toto patrí výrobe a upravuje to rozpočtár alebo konštruktér — ty to vidíš len na čítanie.",
  cennikVyroby:
    "Cenník výroby, ceny operácií, DPH a réžiu upravuje rozpočtár — ty ich vidíš len na čítanie.",
  cennikTovaru: "Cenník tovaru upravuje obchodník — ty ho vidíš len na čítanie.",
  aktivnyTovar: "Ktorý tovar ide do ceny zákazky, vyberá obchodník.",
} as const;

/** Prečo nesmie meniť štruktúru varianty — `null` = smie. */
export function zamokVarianty(
  prava: Prava,
  jeTovar: boolean,
  jeVyrobna = false,
): string | null {
  const rezim = rezimVarianty(prava, jeTovar, jeVyrobna);
  if (rezim === "plny") return null;
  return vetaZamku(rezim, jeTovar, jeVyrobna);
}

/** Veta k zamknutej variante podľa režimu — ako `_veta_citania` na serveri. */
export function vetaZamku(rezim: RezimVarianty, jeTovar: boolean, jeVyrobna = false): string {
  if (jeTovar) return VETA.tovar;
  if (rezim === "len_ceny") return VETA.nabytok;
  return jeVyrobna ? VETA.vyroba : VETA.vypocet;
}

/** Smie zapisovať do cenníka položky (tovar / výroba)? */
export function smieCennik(prava: Prava, tovar: boolean): boolean {
  return tovar ? prava.cennik_tovaru : prava.cennik_vyroby;
}

/** Názov roly do UI. */
export function nazovRoly(rola: string | null | undefined, jeAdmin = false): string {
  if (jeAdmin) return "Admin";
  if (rola === "obchodnik") return "Obchodník";
  if (rola === "konstrukter") return "Konštruktér";
  return "Rozpočtár";
}
